import { useState, useEffect } from "react";
import { FileText, Brain, Target, CheckCircle2 } from "lucide-react";
import quentariLogo from "@/assets/quentari-logo.png";

interface ReportLoadingScreenProps {
  isGenerating: boolean;
}

const LOADING_STEPS = [
  {
    id: 1,
    label: "Analyse verdichten",
    description: "Wir analysieren deine Entscheidungssituation …",
    icon: Brain,
    duration: 3000,
  },
  {
    id: 2,
    label: "Optionen priorisieren",
    description: "Wir priorisieren deine strategischen Optionen …",
    icon: Target,
    duration: 4000,
  },
  {
    id: 3,
    label: "Aktionsempfehlung erstellen",
    description: "Wir generieren deinen Executive-Report …",
    icon: FileText,
    duration: 5000,
  },
];

export const ReportLoadingScreen = ({ isGenerating }: ReportLoadingScreenProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isGenerating) return;

    // Progress animation
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) return 95; // Cap at 95% until complete
        return prev + 0.5;
      });
    }, 100);

    // Step progression
    const stepTimers: NodeJS.Timeout[] = [];
    let accumulatedTime = 0;

    LOADING_STEPS.forEach((step, index) => {
      const timer = setTimeout(() => {
        setCurrentStep(index);
      }, accumulatedTime);
      stepTimers.push(timer);
      accumulatedTime += step.duration;
    });

    return () => {
      clearInterval(progressInterval);
      stepTimers.forEach(clearTimeout);
    };
  }, [isGenerating]);

  return (
    <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center">
      {/* Background Grid Pattern */}
      <div className="fixed inset-0 pointer-events-none">
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
        {/* Radial gradients */}
        <div 
          className="absolute top-0 left-0 w-full h-full opacity-[0.05]"
          style={{
            backgroundImage: `
              radial-gradient(ellipse 60% 40% at 20% 30%, hsl(var(--primary)) 0%, transparent 50%),
              radial-gradient(ellipse 50% 50% at 80% 70%, hsl(var(--primary)) 0%, transparent 50%)
            `,
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-lg mx-auto px-6">
        <div className="text-center space-y-8">
          {/* Logo & Tagline */}
          <div className="space-y-3">
            <img 
              src={quentariLogo} 
              alt="Quentari" 
              className="h-12 w-auto mx-auto"
            />
            <p className="text-sm text-muted-foreground font-medium">
              Quentari strukturiert deine Entscheidung
            </p>
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-primary to-primary/80 transition-all duration-300 ease-out rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {Math.round(progress)}% abgeschlossen
            </p>
          </div>

          {/* Steps Timeline */}
          <div className="space-y-4">
            {LOADING_STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              
              return (
                <div 
                  key={step.id}
                  className={`
                    flex items-center gap-4 p-4 rounded-xl border transition-all duration-500
                    ${isActive 
                      ? 'bg-primary/5 border-primary/30 shadow-lg shadow-primary/5' 
                      : isCompleted 
                        ? 'bg-emerald-500/5 border-emerald-500/20' 
                        : 'bg-muted/30 border-border/50 opacity-50'
                    }
                  `}
                >
                  {/* Step Icon */}
                  <div className={`
                    w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-all duration-300
                    ${isActive 
                      ? 'bg-primary/15 text-primary' 
                      : isCompleted 
                        ? 'bg-emerald-500/15 text-emerald-500' 
                        : 'bg-muted text-muted-foreground'
                    }
                  `}>
                    {isCompleted ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : (
                      <Icon className={`h-5 w-5 ${isActive ? 'animate-pulse' : ''}`} />
                    )}
                  </div>

                  {/* Step Content */}
                  <div className="flex-1 text-left min-w-0">
                    <p className={`
                      text-sm font-semibold transition-colors
                      ${isActive 
                        ? 'text-foreground' 
                        : isCompleted 
                          ? 'text-emerald-600 dark:text-emerald-400' 
                          : 'text-muted-foreground'
                      }
                    `}>
                      {step.label}
                    </p>
                    {isActive && (
                      <p className="text-xs text-muted-foreground mt-0.5 animate-fade-in">
                        {step.description}
                      </p>
                    )}
                  </div>

                  {/* Step Number */}
                  <div className={`
                    text-xs font-bold shrink-0 transition-colors
                    ${isActive 
                      ? 'text-primary' 
                      : isCompleted 
                        ? 'text-emerald-500' 
                        : 'text-muted-foreground/50'
                    }
                  `}>
                    {index + 1}/3
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom Tagline */}
          <p className="text-xs text-muted-foreground/70">
            Wir bereiten gerade deinen Executive-Report vor.
          </p>
        </div>
      </div>
    </div>
  );
};